import React, { useEffect, useState } from 'react';
import { Loader2, PackagePlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { errText, inwardEntries, receivePacks } from '@/services/tissueJwApi';

const NONE = '__none__';

// Record FG packs returned by the jobber; each pack gets a new barcode label.
export const ReceivePacksCard = ({ order, onReceived }) => {
  const [rows, setRows] = useState({});
  const [entry, setEntry] = useState(NONE);
  const [entries, setEntries] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { inwardEntries().then((r) => setEntries(r.entries || [])).catch(() => {}); }, []);

  const set = (sort, k, v) => setRows((r) => ({ ...r, [sort]: { pcs_per_pack: 1, ...r[sort], [k]: v } }));
  const lines = (order.lines || []).map((l) => ({ sort_no: l.sort_no, packs: Number(rows[l.sort_no]?.packs || 0),
    pcs_per_pack: Number(rows[l.sort_no]?.pcs_per_pack || 1) })).filter((l) => l.packs > 0);

  const submit = async () => {
    setSaving(true);
    try {
      const r = await receivePacks(order.jw_id, { lines, inward_entry_id: entry === NONE ? null : entry });
      toast.success(`${r.receipt.packs} pack(s) received — print the pack labels below`);
      (r.warnings || []).forEach((w) => toast.warning(w));
      setRows({});
      onReceived?.(r.receipt);
    } catch (e) { toast.error(errText(e, 'Could not receive packs')); } finally { setSaving(false); }
  };

  return (
    <Card data-testid="receive-packs-card">
      <CardHeader><CardTitle className="text-base flex items-center gap-2"><PackagePlus className="w-4 h-4" /> Receive packs from jobber</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {(order.lines || []).map((l) => (
          <div key={l.sort_no} className="grid grid-cols-[1fr_110px_110px] gap-3 items-end" data-testid={`receive-row-${l.sort_no}`}>
            <div className="min-w-0">
              <p className="text-sm font-medium truncate">{l.design_name || l.fg_name}</p>
              <p className="text-[11px] text-gray-500 font-mono">FG {l.fg_name} · sort {l.sort_no} · sent {l.sent_pcs || 0} pcs</p>
            </div>
            <div><Label className="text-xs">Packs</Label>
              <Input type="number" min="0" value={rows[l.sort_no]?.packs || ''} onChange={(e) => set(l.sort_no, 'packs', e.target.value)}
                data-testid={`receive-packs-${l.sort_no}`} /></div>
            <div><Label className="text-xs">Pcs per pack</Label>
              <Input type="number" min="1" value={rows[l.sort_no]?.pcs_per_pack || 1} onChange={(e) => set(l.sort_no, 'pcs_per_pack', e.target.value)}
                data-testid={`receive-pcs-per-pack-${l.sort_no}`} /></div>
          </div>
        ))}
        <div className="flex flex-wrap items-end gap-3 pt-2">
          <div className="min-w-[260px]"><Label className="text-xs">Gate Inward entry (optional)</Label>
            <Select value={entry} onValueChange={setEntry}>
              <SelectTrigger data-testid="receive-inward-entry"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>Not linked</SelectItem>
                {entries.map((e) => <SelectItem key={e.entry_id} value={e.entry_id}>{e.entry_id} · {e.vendor_name}</SelectItem>)}
              </SelectContent>
            </Select></div>
          <Button disabled={!lines.length || saving} onClick={submit} className="bg-[#2D5F4F] hover:bg-[#234A3D]" data-testid="receive-packs-submit">
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Receive {lines.reduce((a, l) => a + l.packs, 0) || ''} pack(s)
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ReceivePacksCard;
